import classNames from "classnames";
import { forwardRef } from "react";
import { twMerge } from "tailwind-merge";
import Spinner from "@/components/UI/Spinner/Spinner";

// See: https://mui.com/material-ui/react-button/
// See: https://tailwindui.com/components/application-ui/elements/buttons

const ButtonColor = {
  error500: "error500",
  neutral0: "neutral0",
  neutral300: "neutral300",
  neutral500: "neutral500",
  neutral900: "neutral900",
  primary100: "primary100",
  primary500: "primary500",
  primary700: "primary700",
  success500: "success500",
  warning500: "warning500",
} as const;

export const ButtonSize = {
  small2x: "small2x",
  small: "small",
  medium: "medium",
  large: "large",
} as const;

export const ButtonVariant = {
  outlined: "outlined",
  solid: "solid",
  text: "text",
} as const;

const ButtonColorConfig = {
  [ButtonVariant.outlined]: {
    [ButtonColor.error500]: "bg-transparent border-vpRed-500 hover:enabled:bg-vpRed-500/10 text-vpRed-500",
    [ButtonColor.neutral0]: "bg-transparent border-white hover:enabled:bg-white/10 text-white",
    [ButtonColor.neutral300]: "bg-transparent border-vpGray-300 hover:enabled:bg-vpGray-300/10 text-vpGray-300",
    [ButtonColor.neutral500]: "bg-transparent border-vpGray-500 hover:enabled:bg-vpGray-500/10 text-vpGray-500",
    [ButtonColor.neutral900]: "bg-transparent border-vpGray-900 hover:enabled:bg-vpGray-900/10 text-vpGray-900",
    [ButtonColor.primary100]: "bg-transparent border-vpGray-100 hover:enabled:bg-vpBlue-100/10 text-vpBlue-700",
    [ButtonColor.primary500]: "bg-transparent border-vpBlue-500 hover:enabled:bg-vpBlue-500/10 text-vpBlue-500",
    [ButtonColor.primary700]: "bg-transparent border-vpBlue-700 hover:enabled:bg-vpBlue-700/10 text-vpBlue-700",
    [ButtonColor.success500]: "bg-transparent border-vpTeal-500 hover:enabled:bg-vpTeal-500/10 text-vpTeal-500",
    [ButtonColor.warning500]: "bg-transparent border-vpOrange-500 hover:enabled:bg-vpOrange-500/10 text-vpOrange-500",
  },
  [ButtonVariant.solid]: {
    [ButtonColor.error500]: "bg-vpRed-500 hover:enabled:bg-vpRed-700 text-white",
    [ButtonColor.neutral0]: "bg-white hover:enabled:bg-vpGray-200 text-white",
    [ButtonColor.neutral300]: "bg-vpGray-300 hover:enabled:bg-vpGray-500 text-white",
    [ButtonColor.neutral500]: "bg-vpGray-500 hover:enabled:bg-vpGray-700 text-white",
    [ButtonColor.neutral900]: "bg-vpGray-900 hover:enabled:bg-vpGray-900 text-white",
    [ButtonColor.primary100]: "bg-vpBlue-100 hover:enabled:bg-vpBlue-300 text-vpBlue-700",
    [ButtonColor.primary500]: "bg-vpBlue-500 hover:enabled:bg-vpBlue-700 text-white",
    [ButtonColor.primary700]: "bg-vpBlue-700 hover:enabled:bg-vpBlue-900 text-white",
    [ButtonColor.success500]: "bg-vpTeal-500 hover:enabled:bg-vpTeal-700 text-white",
    [ButtonColor.warning500]: "bg-vpOrange-500 hover:enabled:bg-vpOrange-700 text-white",
  },
  [ButtonVariant.text]: {
    [ButtonColor.error500]: "hover:enabled:bg-vpRed-700/10 text-vpRed-500",
    [ButtonColor.neutral0]: "hover:enabled:bg-white/10 text-white",
    [ButtonColor.neutral300]: "hover:enabled:bg-vpGray-700/10 text-vpGray-300",
    [ButtonColor.neutral500]: "hover:enabled:bg-vpGray-700/10 text-vpGray-500",
    [ButtonColor.neutral900]: "hover:enabled:bg-vpGray-700/10 text-vpGray-900",
    [ButtonColor.primary100]: "hover:enabled:bg-vpBlue-500/10 text-vpBlue-700",
    [ButtonColor.primary500]: "hover:enabled:bg-vpBlue-500/10 text-vpBlue-500",
    [ButtonColor.primary700]: "hover:enabled:bg-vpBlue-700/10 text-vpBlue-700",
    [ButtonColor.success500]: "hover:enabled:bg-vpTeal-700/10 text-vpTeal-500",
    [ButtonColor.warning500]: "hover:enabled:bg-vpOrange-700/10 text-vpOrange-500",
  },
} as const;

const ButtonSizeConfig = {
  [ButtonSize.small2x]: {
    root: "px-2 py-1 text-sm",
    spinner: "sm",
  },
  [ButtonSize.small]: {
    root: "px-4 py-2 text-sm",
    spinner: "sm",
  },
  [ButtonSize.medium]: {
    root: "px-6 py-3 text-md",
    spinner: "md",
  },
  [ButtonSize.large]: {
    root: "px-8 py-4 text-lg",
    spinner: "md",
  },
} as const;

export type ButtonRootNode = HTMLButtonElement;

export interface ButtonProps extends React.HTMLAttributes<ButtonRootNode> {
  color?: keyof typeof ButtonColor;
  dataCy?: string;
  disabled?: boolean;
  endNode?: React.ReactNode;
  loading?: boolean;
  size?: keyof typeof ButtonSize;
  slots?: {
    root?: {
      css?: string;
    };
    spinner?: {
      css?: string;
    };
  };
  startNode?: React.ReactNode;
  variant?: keyof typeof ButtonVariant;
}

const Button = forwardRef<ButtonRootNode, ButtonProps>(
  (
    {
      className,
      children,
      color = ButtonColor.primary500,
      dataCy,
      disabled,
      endNode,
      loading,
      size = ButtonSize.medium,
      slots,
      startNode,
      variant = ButtonVariant.solid,
      ...props
    },
    ref
  ) => {
    return (
      <button
        className={twMerge(
          classNames(
            "flex items-center justify-center whitespace-nowrap rounded-md border border-transparent font-medium transition-all",
            ButtonSizeConfig[size].root,
            ButtonColorConfig[variant][color],
            {
              "opacity-50": disabled,
            },
            className,
            slots?.root?.css
          )
        )}
        data-cy={dataCy}
        disabled={disabled}
        ref={ref}
        {...props}
      >
        {startNode && !loading && <span className="mr-2 flex items-center">{startNode}</span>}
        {loading ? (
          <Spinner className={twMerge(slots?.spinner?.css)} size={ButtonSizeConfig[size].spinner} />
        ) : (
          children
        )}
        {endNode && !loading && <span className="ml-2 flex items-center">{endNode}</span>}
      </button>
    );
  }
);

Button.displayName = Button.name;

export default Button;
